import React, { useEffect, useState, useContext } from 'react';
import { Link } from 'react-router-dom';
import api from '../utils/api';
import { ProjectContext } from '../contexts/ProjectContext';
import { toast } from 'react-toastify';
import EstimatesList from '../components/EstimatesList';
import EstimateUploader from '../components/EstimateUploader';
import { useOrg } from '../contexts/OrgContext';

export default function ProjectsPage() {
  const { setActiveProject } = useContext(ProjectContext);
  const { permissions } = useOrg();
  const [projects, setProjects] = useState([]);
  const [confirmId, setConfirmId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey(k => k + 1);

  // load
  useEffect(() => {
    api.get('/projects')
      .then(res => setProjects(res.data))
      .catch(err => console.error('Failed to load projects', err));
  }, []);

  // delete
  const askDelete = (e, id) => {
    if (!permissions.canDeleteProject) return;
    e.preventDefault();
    e.stopPropagation();
    setConfirmId(id);
  };
  const cancel = () => setConfirmId(null);
  const confirm = async (id) => {
    try {
      await api.delete(`/projects/${id}`);
      setProjects(prev => prev.filter(p => (p._id || p.id) !== id));
      toast.success('🗑️ Project deleted');
    } catch (err) {
      console.error(err);
      toast.error('🚨 Could not delete project');
    } finally {
      setConfirmId(null);
    }
  };

  // design tokens
  const card =
    'glass-card relative group transition ring-0 hover:ring-1 hover:ring-amber-400/40';
  const pill =
    'glass-ghost text-sm px-5 py-3';
  const delBtn =
    'absolute bottom-2 right-2 opacity-0 group-hover:opacity-100 transition ' +
    'grid place-items-center w-8 h-8 rounded-md border border-red-400/40 ' +
    'bg-red-500/20 text-red-100 hover:bg-red-500/30 focus:outline-none ' +
    'focus-visible:ring-2 focus-visible:ring-red-400/70';
  const confirmWrap =
    'absolute bottom-2 right-2 flex gap-2 opacity-100 transition';
  const confirmYes =
    'px-3 py-1.5 rounded-md text-xs font-semibold text-red-50 bg-red-600/90 ' +
    'hover:bg-red-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400/70';
  const confirmNo =
    'px-3 py-1.5 rounded-md text-xs font-medium text-slate-100 bg-white/10 border border-white/10 ' +
    'hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400/70';

  return (
    <div className="mx-auto max-w-5xl px-6 py-10 text-zinc-200">
      <h1 className="mb-6 text-3xl font-semibold tracking-tight">
        <span className="bg-gradient-to-r from-amber-200 to-amber-500 bg-clip-text text-transparent">
          Your Projects
        </span>
      </h1>

      {projects.length === 0 && (
        <p className="text-zinc-400">No projects yet.</p>
      )}

      <ul className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {projects.map((p) => {
          const id = p._id || p.id;
          const confirming = confirmId === id;
          return (
            <li key={id} className={card}>
              <Link
                to={`/projects/${id}/estimate`}
                onClick={() => setActiveProject(p)}
                className={`${pill} w-full h-full text-center`}
              >
                <div className="w-full py-6">
                  <div className="truncate text-base font-semibold">{p.name}</div>
                </div>
              </Link>

              {!confirming ? (
                <button
                  onClick={(e) => askDelete(e, id)}
                  className={`${delBtn} ${!permissions.canDeleteProject ? 'opacity-50 cursor-not-allowed' : ''}`}
                  aria-label={`Delete project ${p.name}`}
                  title="Delete"
                  disabled={!permissions.canDeleteProject}
                >
                  {/* trash icon */}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M3 6h18" stroke="currentColor" strokeWidth="2"/>
                    <path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" stroke="currentColor" strokeWidth="2"/>
                    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" stroke="currentColor" strokeWidth="2"/>
                    <path d="M10 11v6" stroke="currentColor" strokeWidth="2"/>
                    <path d="M14 11v6" stroke="currentColor" strokeWidth="2"/>
                  </svg>
                </button>
              ) : (
                <div className={confirmWrap}>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); confirm(id); }}
                    className={confirmYes}
                  >
                    Yes
                  </button>
                  <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); cancel(); }}
                    className={confirmNo}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </li>
          );
        })}
      </ul>

    {permissions.canAccessEstimates && (
      <div className="mt-10 grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="glass-card p-6">
          <h3 className="text-xl font-semibold mb-3">
            <span className="bg-gradient-to-r from-amber-200 to-amber-500 bg-clip-text text-transparent">
              Your & Team Estimates
            </span>
          </h3>
          <EstimatesList key={refreshKey} />
        </div>

        <div className="glass-card p-6">
          <h3 className="text-xl font-semibold mb-3">
            <span className="bg-gradient-to-r from-amber-200 to-amber-500 bg-clip-text text-transparent">
              Upload New Estimate
            </span>
          </h3>
          {permissions.canUpload ? (
            <EstimateUploader onUploaded={refresh} />
          ) : (
            <p className="text-sm text-zinc-500">
              Your current role cannot upload estimates. Ask an administrator to grant access.
            </p>
          )}
        </div>
      </div>
    )}
  </div>
);
}
